/**
 * 📖 **우리 아이 읽기 기록** — 이 기기 안에만 남는다 (2026-10-24 · 리서치 최종안 ②).
 *
 * ## 왜
 * 보호자는 아이가 무엇을 읽었는지 모른다. 경쟁 서비스(아이들나라 독서 기록장 등)는 서버에 모아 리포트를 보낸다 —
 * 우리는 **무수집**(불변식 1)이라 같은 것을 **기기 안에서만** 보여 준다. 서버는 이런 게 있는 줄도 모른다.
 *
 * ## 무엇을 남기나 (책마다)
 * 가장 멀리 읽은 쪽 · 다 읽은 횟수(같은 날은 한 번) · 마법을 연 횟수 · 읽은 날(최근 30일). 20권까지.
 * 「읽던 자리」(readingProgress — 마지막 한 권)와는 따로다 — 그건 「이어서 읽기」용이다.
 *
 * ## 지우는 길
 * 보호자 화면의 「이 기록 지우기」, 그리고 설정의 「이 기기 저장값 지우기」(gc_ 접두사 · lib/resetLocal).
 *
 * ## 저장소가 막힌 브라우저
 * 사생활 보호 창 등에서 localStorage 가 던질 수 있다 — 그때는 **기록 없이** 책이 그대로 돈다. 책 읽기를 막을 일이 아니라
 * 이 경계에서만 받는다(readingProgress 와 같은 판단).
 */
const KEY = "gc_reading_log_v1";
export const LOG_DAYS = 30;
const MAX_BOOKS = 20;

export interface BookReadLog {
  /** 가장 멀리 읽은 쪽(사람이 세는 번호) */
  maxPage: number;
  /** 책 전체 쪽 수(그때 본 값) */
  total: number;
  /** 다 읽은 횟수 — 같은 날 여러 번 끝까지 가도 한 번 */
  finished: number;
  /** 마지막으로 다 읽은 날(YYYY-MM-DD) — 같은 날 두 번 세지 않으려고 */
  finishedDay?: string;
  /** 마법을 연 횟수 */
  magic: number;
  /** 읽은 날(YYYY-MM-DD) — 최근 LOG_DAYS 일만 */
  days: string[];
  /** 마지막으로 읽은 시각(ms) */
  lastAt: number;
}
export type ReadingLog = Record<string, BookReadLog>;

/** 이 기기 달력의 날짜(YYYY-MM-DD) — 한국 아침에 어제로 찍히지 않게 기기 시간대로 센다 */
export function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const num = (v: unknown, min = 0) => (typeof v === "number" && Number.isFinite(v) && v >= min ? Math.floor(v) : null);

/** 저장된 값은 바깥 입력처럼 모양을 확인한다(손상·옛 모양은 버린다) */
function clean(raw: unknown): ReadingLog {
  const out: ReadingLog = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!/^[\w-]{1,60}$/.test(id) || !v || typeof v !== "object") continue;
    const o = v as Record<string, unknown>;
    const maxPage = num(o.maxPage, 1);
    const lastAt = num(o.lastAt, 1);
    if (maxPage === null || lastAt === null) continue;
    out[id] = {
      maxPage,
      total: num(o.total, 0) ?? 0,
      finished: num(o.finished, 0) ?? 0,
      finishedDay: typeof o.finishedDay === "string" ? o.finishedDay : undefined,
      magic: num(o.magic, 0) ?? 0,
      days: Array.isArray(o.days) ? o.days.filter((d): d is string => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) : [],
      lastAt,
    };
  }
  return out;
}

function read(): ReadingLog {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? clean(JSON.parse(raw)) : {};
  } catch {
    return {}; // 막힌 저장소·깨진 값 — 기록 없이(위 설명)
  }
}

function write(log: ReadingLog): void {
  // 오래 안 읽은 책부터 덜어 20권까지만
  const keep = Object.entries(log)
    .sort((a, b) => b[1].lastAt - a[1].lastAt)
    .slice(0, MAX_BOOKS);
  try {
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(keep)));
  } catch {
    /* 막힌 저장소 — 기록 없이(위 설명) */
  }
}

/** 최근 LOG_DAYS 일 안의 날만 남긴다 */
function recentDays(days: string[], now: number): string[] {
  const cut = dayKey(now - LOG_DAYS * 86400000);
  return [...new Set(days)].filter((d) => d > cut).sort();
}

function touch(log: ReadingLog, bookId: string, now: number): BookReadLog {
  const cur = log[bookId] ?? { maxPage: 1, total: 0, finished: 0, magic: 0, days: [], lastAt: now };
  cur.days = recentDays([...cur.days, dayKey(now)], now);
  cur.lastAt = now;
  log[bookId] = cur;
  return cur;
}

/** 쪽을 볼 때 */
export function notePage(bookId: string, page: number, total: number, now = Date.now()): void {
  if (!bookId || !Number.isFinite(page) || page < 1) return;
  const log = read();
  const b = touch(log, bookId, now);
  b.maxPage = Math.max(b.maxPage, Math.floor(page));
  if (Number.isFinite(total) && total > 0) b.total = Math.floor(total);
  write(log);
}

/** 마지막 쪽에 닿았을 때 — 같은 날은 한 번만 센다(앞뒤로 넘기다 끝을 여러 번 지나도) */
export function noteFinished(bookId: string, now = Date.now()): void {
  if (!bookId) return;
  const log = read();
  const b = touch(log, bookId, now);
  const today = dayKey(now);
  if (b.finishedDay !== today) {
    b.finished += 1;
    b.finishedDay = today;
  }
  if (b.total > 0) b.maxPage = Math.max(b.maxPage, b.total);
  write(log);
}

/** 마법을 열 때 */
export function noteMagic(bookId: string, now = Date.now()): void {
  if (!bookId) return;
  const log = read();
  touch(log, bookId, now).magic += 1;
  write(log);
}

/** 보호자 화면이 그릴 목록 — 최근에 읽은 책부터. 읽은 날은 최근 LOG_DAYS 일로 다시 거른다 */
export function readingLog(now = Date.now()): (BookReadLog & { bookId: string })[] {
  return Object.entries(read())
    .map(([bookId, b]) => ({ ...b, bookId, days: recentDays(b.days, now) }))
    .sort((a, b) => b.lastAt - a.lastAt);
}

/** 이 기록만 지운다(보호자 화면) */
export function clearReadingLog(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 막힌 저장소 — 지울 것도 없다 */
  }
}
